import React, { useState } from 'react'
import injectSheet from 'react-jss'
import Button from 'pobble-components/dist/button'
import ButtonElement from 'pobble-components/dist/button-element'
import { Modal, ModalContent, ModalFooter } from 'pobble-components/dist/modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown } from '@fortawesome/pro-solid-svg-icons/faAngleDown'
import { faAngleUp } from '@fortawesome/pro-solid-svg-icons/faAngleUp'
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay'
import { PobbleCookie } from 'pobble-components/dist/cookies'
import posthog from 'posthog-js'
import videoThumb from './illustrations/how-to-use-pobble-video-thumb.jpg'
import { PARENT_PRIMARY_CTA } from './parent-theme'

const PARENT_HOW_TO_USE_MINIMISED = new PobbleCookie(
  'PARENT_HOW_TO_USE_MINIMISED'
)
const MINIMISED_COOKIE_DAYS = 30

const STEPS = [
  'Pick a picture prompt',
  'Talk about it together',
  'Let them write at their own pace',
  "Celebrate what they've written"
]

const styles = {
  panel: {
    position: 'relative',
    marginBottom: '32px',
    padding: '24px 28px',
    borderRadius: '8px',
    background: 'var(--purple)',
    color: '#fff',
    '@media (min-width: 1024px)': {
      marginBottom: '64px',
      padding: '32px 36px'
    }
  },
  panelMinimised: {
    padding: '18px 24px',
    '@media (min-width: 1024px)': {
      padding: '20px 32px'
    }
  },
  toggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: 0,
    border: 'none',
    background: 'none',
    color: '#fff',
    font: 'var(--body)',
    fontWeight: 600,
    fontSize: '15px',
    cursor: 'pointer',
    flexShrink: 0,
    '&:hover': {
      opacity: 0.85
    }
  },
  toggleExpanded: {
    position: 'absolute',
    top: '18px',
    right: '20px',
    '@media (min-width: 1024px)': {
      top: '24px',
      right: '28px'
    }
  },
  collapsedContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px'
  },
  collapsedText: {
    margin: 0,
    font: 'var(--body)',
    fontSize: '15px',
    lineHeight: 1.4,
    '& strong': {
      fontWeight: 700
    }
  },
  expandedLayout: {
    display: 'grid',
    gap: '28px',
    alignItems: 'center',
    paddingRight: '72px',
    '@media (min-width: 900px)': {
      gridTemplateColumns: '1fr minmax(220px, 340px)',
      gap: '36px'
    }
  },
  copy: {
    minWidth: 0
  },
  title: {
    margin: '0 0 12px',
    font: 'var(--heading)',
    fontSize: '28px',
    lineHeight: 1.15,
    color: '#fff',
    '@media (min-width: 1024px)': {
      fontSize: '32px',
      marginBottom: '16px'
    }
  },
  intro: {
    margin: '0 0 8px',
    font: 'var(--body)',
    fontSize: '16px',
    lineHeight: 1.5
  },
  videoHint: {
    margin: '0 0 20px',
    font: 'var(--body)',
    fontSize: '16px',
    lineHeight: 1.5
  },
  steps: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    display: 'grid',
    gap: '8px'
  },
  step: {
    display: 'flex',
    gap: '10px',
    font: 'var(--body)',
    fontSize: '16px',
    lineHeight: 1.45
  },
  stepNumber: {
    flexShrink: 0,
    fontWeight: 700
  },
  videoLink: {
    display: 'block',
    position: 'relative',
    width: '100%',
    padding: 0,
    border: 'none',
    borderRadius: '12px',
    overflow: 'hidden',
    background: 'none',
    cursor: 'pointer',
    '&:hover $playButton': {
      transform: 'translate(-50%, -50%) scale(1.05)'
    }
  },
  videoThumb: {
    display: 'block',
    width: '100%',
    height: 'auto',
    aspectRatio: '16 / 10',
    objectFit: 'cover'
  },
  playButton: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: '#fff',
    color: 'var(--gray)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    paddingLeft: '4px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
    transition: 'transform 0.15s ease'
  },
  videoModalContent: {
    width: '300px',
    '@media (min-width: 1024px)': {
      width: '1000px'
    },
    textAlign: 'center',
    '& p': {
      margin: 0,
      color: '#4b5563',
      font: 'var(--body)',
      lineHeight: 1.5
    }
  },
  videoWrapper: {
    position: 'relative',
    aspectRatio: '16 / 9',
    width: '100%',
    '& iframe': {
      position: 'absolute',
      inset: 0,
      border: 0,
      width: '100%',
      height: '100%'
    }
  },
  videoModalClose: PARENT_PRIMARY_CTA
}

export const ParentHowToUsePanel = injectSheet(styles)(
  function ParentHowToUsePanel({ classes }) {
    const [minimised, setMinimised] = useState(
      () => PARENT_HOW_TO_USE_MINIMISED.get() === '1'
    )
    const [videoModalOpen, setVideoModalOpen] = useState(false)

    const openVideoModal = () => {
      posthog.capture('opened parent how to use video modal')
      setVideoModalOpen(true)
    }

    const closeVideoModal = () => {
      posthog.capture('closed parent how to use video modal')
      setVideoModalOpen(false)
    }

    const handleToggle = () => {
      if (minimised) {
        posthog.capture('clicked on expand parent how to use banner')
        PARENT_HOW_TO_USE_MINIMISED.remove()
        setMinimised(false)
        return
      }

      posthog.capture('clicked on hide parent how to use banner')
      PARENT_HOW_TO_USE_MINIMISED.set('1', { expires: MINIMISED_COOKIE_DAYS })
      setMinimised(true)
    }

    if (minimised) {
      return (
        <div className={`${classes.panel} ${classes.panelMinimised}`}>
          <div className={classes.collapsedContent}>
            <p className={classes.collapsedText}>
              <strong>How to use Pobble Home</strong> &bull; 4 simple steps to
              get your child writing
            </p>
            <ButtonElement className={classes.toggle} onClick={handleToggle}>
              Expand
              <FontAwesomeIcon icon={faAngleDown} />
            </ButtonElement>
          </div>
        </div>
      )
    }

    return (
      <div className={classes.panel}>
        <ButtonElement
          className={`${classes.toggle} ${classes.toggleExpanded}`}
          onClick={handleToggle}>
          Hide
          <FontAwesomeIcon icon={faAngleUp} />
        </ButtonElement>
        <div className={classes.expandedLayout}>
          <div className={classes.copy}>
            <h4 className={classes.title}>How to use Pobble</h4>
            <p className={classes.intro}>
              Four simple steps to get your child writing.
            </p>
            <p className={classes.videoHint}>
              Watch the 60-second video to see how it works.
            </p>
            <ol className={classes.steps}>
              {STEPS.map((step, index) => (
                <li key={step} className={classes.step}>
                  <span className={classes.stepNumber}>{index + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
          <ButtonElement
            className={classes.videoLink}
            aria-label="Watch how to use Pobble"
            onClick={openVideoModal}>
            <img className={classes.videoThumb} src={videoThumb} alt="" />
            <span className={classes.playButton} aria-hidden="true">
              <FontAwesomeIcon icon={faPlay} />
            </span>
          </ButtonElement>
        </div>
        <Modal isOpen={videoModalOpen} requestClose={closeVideoModal}>
          <ModalContent className={classes.videoModalContent}>
            <div className={classes.videoWrapper}>
              <iframe
                src={'https://player.vimeo.com/video/1207809046?autoplay=1'}
                title="Video player"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                border="0"
              />
            </div>
          </ModalContent>
          <ModalFooter alignRight>
            <Button
              className={classes.videoModalClose}
              tall
              minw
              action={closeVideoModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    )
  }
)
